"use client";

import { useEffect, useRef } from "react";

import { useViewer } from "@/hooks/use-viewer";
import { syncAnalyticsViewer } from "@/lib/analytics";
import { setClientAttribution } from "@/lib/marketing-context";
import { configureGoogleTag } from "@/lib/site-tracking/configureGoogleTag";

/** Synchronizes analytics identity on app reopen, login, logout, and account changes. */
export function useSyncAnalyticsViewer(): void {
  const viewer = useViewer();
  useEffect(() => {
    setClientAttribution(viewer.attribution);
    try {
      window.localStorage.removeItem("garimpa:marketing-attribution:v1");
      window.sessionStorage.removeItem("garimpa:marketing-attribution-session:v1");
    } catch {
      /* Browser storage can be disabled. */
    }
  }, [viewer.attribution]);
  const userType =
    viewer.identity === "visitor"
      ? "anonymous"
      : viewer.identity === "guest"
        ? "guest"
        : viewer.user.role === "admin"
          ? "admin"
          : "member";
  const locale = viewer.preferences.locale;
  const viewerKey = `${viewer.userId ?? ""}:${userType}:${locale}`;
  const previousViewerKey = useRef<string | null>(null);

  useEffect(() => {
    configureGoogleTag();
    if (viewerKey === previousViewerKey.current) return;
    previousViewerKey.current = viewerKey;

    syncAnalyticsViewer({ userId: viewer.userId, userType, locale });
  }, [locale, userType, viewer.userId, viewerKey]);
}
